import { Controller } from "@hotwired/stimulus";

import Chart from "chart.js/auto";

function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function withAlpha(color, alpha) {
  const match = color.match(/rgba?\(([^)]+)\)/i);
  if (!match) {
    return color;
  }
  const [r, g, b] = match[1].split(",").map((part) => part.trim());
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default class extends Controller {
  static targets = ["container"];

  initialize() {
    const revenueData = JSON.parse(this.containerTarget.dataset.revenueData);
    const labels = revenueData.map((data) => data.date);
    const revenueValues = revenueData.map((data) => data.revenue);
    const formatType = this.containerTarget.dataset.chartFormatType || "currency";

    // If a chart already exists, remove it.
    const previousChart = this.containerTarget.getElementsByTagName("canvas")[0];
    if (previousChart) {
      previousChart.parentElement.removeChild(previousChart);
    }

    this.generateChart(labels, revenueValues, formatType);
  }

  generateChart(labels, revenueValues, formatType = "currency") {
    const line = cssVar("--thm-dashboard-chart-line");
    const shade1 = cssVar("--thm-dashboard-chart-shade1");
    const shade2 = cssVar("--thm-dashboard-chart-shade2");
    const dot = cssVar("--thm-dashboard-chart-dot");
    const text = cssVar("--thm-dashboard-chart-text");
    const subText = cssVar("--thm-dashboard-chart-sub-text");
    const box = cssVar("--thm-dashboard-chart-box");

    const canvas = document.createElement("canvas");
    canvas.classList.add(
      "w-full",
      "h-[260px]",
      "xl:h-[310px]",
      "pr-3",
      "pt-2",
      "bg-thm-dashboard-chart-box",
      "border",
      "border-thm-dashboard-chart-box-border",
      "rounded-md"
    );
    this.containerTarget.appendChild(canvas);

    new Chart(canvas, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Value",
            data: revenueValues,
            fill: true,
            backgroundColor: (context) => {
              const ctx = context.chart.ctx;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, withAlpha(shade1, 0.4));
              gradient.addColorStop(0.55, withAlpha(shade1, 0.12));
              gradient.addColorStop(1, shade2 || withAlpha(shade1, 0));
              return gradient;
            },
            borderColor: line,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: dot,
            pointHoverBorderColor: dot,
          },
        ],
      },
      options: {
        interaction: {
          mode: "index",
          intersect: false,
        },
        scales: {
          y: {
            ticks: {
              display: true,
              maxTicksLimit: 4,
              color: subText || text,
            },
            grid: {
              display: true,
              color: withAlpha(line, 0.08),
              drawBorder: false,
            },
            border: {
              width: 0,
            },
          },
          x: {
            ticks: {
              display: true,
              maxTicksLimit: 4,
              color: subText || text,
              callback: function (value) {
                const label = this.getLabelForValue(value);
                const date = new Date(label);

                if (isNaN(date.getTime())) return label;

                const day = date.getDate();
                const month = date.toLocaleString("default", { month: "short" });

                return `${day} ${month}`;
              },
            },
            grid: {
              display: false,
            },
            border: {
              width: 0,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            backgroundColor: withAlpha(box, 0.85) || "rgba(0, 0, 0, 0.6)",
            titleColor: line || text,
            bodyColor: text || "#ffffff",
            displayColors: false,
            callbacks: {
              title: function (tooltipItems) {
                const label = tooltipItems[0].label;
                const date = new Date(label);

                if (isNaN(date.getTime())) return label;

                const day = date.getDate();
                const month = date.toLocaleString("default", { month: "short" });
                return `${day} ${month}`;
              },
              label: function (context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  if (formatType === "number") {
                    label += Math.round(context.parsed.y);
                  } else {
                    label += new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(context.parsed.y);
                  }
                }
                return label;
              },
            },
          },
        },
        layout: {
          padding: 0,
        },
        maintainAspectRatio: false,
      },
    });
  }
}
